// @flow
import type { Element } from 'react';
import React from 'react';
import { Form, Label, Table } from 'reactstrap';
import {
  BUILDING_MODS_COLUMN_PIER_SPANDREL_RATIO,
  BUILDING_MODS_COLUMN_RATIO_LESS_THAN_TYPICAL,
  BUILDING_MODS_CONSTRUCTION_QUALITY,
  BUILDING_MODS_CROSS_GRAIN_OOP_CONN,
  BUILDING_MODS_DIAPHRAGM_DEFICIENCY,
  BUILDING_MODS_DIAPHRAM_OPEN,
  BUILDING_MODS_FLAT_PLATE_AS_BEAM,
  BUILDING_MODS_FULL_STORY_GRADE,
  BUILDING_MODS_FULL_STORY_GRADE_WLF,
  BUILDING_MODS_INTERIOR_WALL_DENSITY,
  BUILDING_MODS_K_BRACING,
  BUILDING_MODS_LIFT_SLAB_CONSTRUCTION,
  BUILDING_MODS_MATERIAL_DETERIORATION,
  BUILDING_MODS_NON_PARALLEL,
  BUILDING_MODS_NON_STRUCTURAL_CONSTRUCTION_QUALITY,
  BUILDING_MODS_OOP_OFFSET,
  BUILDING_MODS_OTHER_PLAN_IRREGULARITY,
  BUILDING_MODS_OTHER_VERTICAL_IRREGULARITY,
  BUILDING_MODS_PANEL_ZONE_DEFICIENCY,
  BUILDING_MODS_POUNDING_DIFFERENT_BUILDING_HEIGHTS,
  BUILDING_MODS_POUNDING_END_OF_BLOCK,
  BUILDING_MODS_POUNDING_FLOORS_UNALIGNED,
  BUILDING_MODS_REDUNDANCY,
  BUILDING_MODS_REENTRANT,
  BUILDING_MODS_SETBACK_INBOARD,
  BUILDING_MODS_SETBACK_IN_PLANE_LFRS,
  BUILDING_MODS_SETBACK_OUTBOARD,
  BUILDING_MODS_SPLIT_LEVEL,
  BUILDING_MODS_STRONG_COLUMN_WEAK_BEAM,
  BUILDING_MODS_WELD_STRENGTH,
  PLAN_TORSION,
  VERTICAL_IRREGULARITY,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import BackButton from '#components/pages/Models/Model/sections/support/buttons/BackButton.jsx';
import NextButton from '#components/pages/Models/Model/sections/support/buttons/NextButton.jsx';
import Tip from '#components/support/tooltips/Tip/index.jsx';
import { Field, FormGroup } from '#components/pages/Models/Model/support/form/index.js';
import { buildingModifierTable } from './index.module.scss';

type Props = {|
  fieldsByName: Object,
  form: string,
  heading: string,
  onBack: Function,
  onNext: Function,
|};

const BuildingModifiersChecklistSection = (props: Props): Element<'div'> => {
  const { fieldsByName, form, heading, onBack, onNext } = props;

  const customRow = (name, labelClass) => (
    <FormGroup tag="tr" form={form} fields={[name]}>
      <td>
        <Label htmlFor={name} className={labelClass || ''}>
          {fieldsByName[name].label} <Tip tipId={name} />
        </Label>
      </td>
      <td>
        <Field field={fieldsByName[name]} excludeLabel />
      </td>
    </FormGroup>
  );

  const constructionQualityRow = (name, labelClass) => customRow(name, labelClass);

  const irregularityRow = (name, labelClass) => customRow(name, labelClass);

  const structuralMaterialDeteriorationRow = (name, labelClass) => customRow(name, labelClass);

  const yesNoRow = (name, labelClass) => customRow(name, labelClass);

  const headerRow = (description, fields) => (
    <FormGroup tag="tr" form={form} fields={fields}>
      <td colSpan="2" className="pt-4 font-weight-bold">
        {description}:
      </td>
    </FormGroup>
  );

  return (
    <div>
      <h2>{heading}</h2>
      <Form noValidate>
        <FormGroup tag="fieldset" className="pb-0">
          <legend>Vertical Irregularities</legend>
          <Table className={buildingModifierTable}>
            <tbody>
              {headerRow('Soft/Weak Story', [VERTICAL_IRREGULARITY])}
              {irregularityRow(VERTICAL_IRREGULARITY, 'pl-4')}
              {headerRow('Sloping Site', [
                BUILDING_MODS_FULL_STORY_GRADE_WLF,
                BUILDING_MODS_FULL_STORY_GRADE,
              ])}
              {yesNoRow(BUILDING_MODS_FULL_STORY_GRADE_WLF, 'pl-4')}
              {yesNoRow(BUILDING_MODS_FULL_STORY_GRADE, 'pl-4')}
              {headerRow('Setback', [
                BUILDING_MODS_SETBACK_OUTBOARD,
                BUILDING_MODS_SETBACK_INBOARD,
                BUILDING_MODS_SETBACK_IN_PLANE_LFRS,
              ])}
              {yesNoRow(BUILDING_MODS_SETBACK_OUTBOARD, 'pl-4')}
              {yesNoRow(BUILDING_MODS_SETBACK_INBOARD, 'pl-4')}
              {yesNoRow(BUILDING_MODS_SETBACK_IN_PLANE_LFRS, 'pl-4')}
              {headerRow('Short Column/Pier', [
                BUILDING_MODS_COLUMN_RATIO_LESS_THAN_TYPICAL,
                BUILDING_MODS_COLUMN_PIER_SPANDREL_RATIO,
              ])}
              {yesNoRow(BUILDING_MODS_COLUMN_RATIO_LESS_THAN_TYPICAL, 'pl-4')}
              {yesNoRow(BUILDING_MODS_COLUMN_PIER_SPANDREL_RATIO, 'pl-4')}
              {headerRow('Split Level', [BUILDING_MODS_SPLIT_LEVEL])}
              {yesNoRow(BUILDING_MODS_SPLIT_LEVEL, 'pl-4')}
              {headerRow('Other Irregularity', [BUILDING_MODS_OTHER_VERTICAL_IRREGULARITY])}
              {irregularityRow(BUILDING_MODS_OTHER_VERTICAL_IRREGULARITY, 'pl-4')}
            </tbody>
          </Table>
        </FormGroup>
        <FormGroup tag="fieldset">
          <legend>Plan Irregularities</legend>
          <Table className={buildingModifierTable}>
            <tbody>
              {irregularityRow(PLAN_TORSION)}
              {yesNoRow(BUILDING_MODS_NON_PARALLEL)}
              {yesNoRow(BUILDING_MODS_REENTRANT)}
              {yesNoRow(BUILDING_MODS_DIAPHRAM_OPEN)}
              {yesNoRow(BUILDING_MODS_OOP_OFFSET)}
              {irregularityRow(BUILDING_MODS_OTHER_PLAN_IRREGULARITY)}
            </tbody>
          </Table>
        </FormGroup>
        <FormGroup tag="fieldset" form={form} fields={[BUILDING_MODS_REDUNDANCY]}>
          <legend>Redundancy</legend>
          <Table className={buildingModifierTable}>
            <tbody>{yesNoRow(BUILDING_MODS_REDUNDANCY)}</tbody>
          </Table>
        </FormGroup>
        <FormGroup
          tag="fieldset"
          form={form}
          fields={[
            BUILDING_MODS_POUNDING_FLOORS_UNALIGNED,
            BUILDING_MODS_POUNDING_DIFFERENT_BUILDING_HEIGHTS,
            BUILDING_MODS_POUNDING_END_OF_BLOCK,
          ]}
        >
          <legend>Pounding</legend>
          <Table className={buildingModifierTable}>
            <tbody>
              {yesNoRow(BUILDING_MODS_POUNDING_FLOORS_UNALIGNED)}
              {yesNoRow(BUILDING_MODS_POUNDING_DIFFERENT_BUILDING_HEIGHTS)}
              {yesNoRow(BUILDING_MODS_POUNDING_END_OF_BLOCK)}
            </tbody>
          </Table>
        </FormGroup>
        <FormGroup
          tag="fieldset"
          form={form}
          fields={[
            BUILDING_MODS_CROSS_GRAIN_OOP_CONN,
            BUILDING_MODS_DIAPHRAGM_DEFICIENCY,
            BUILDING_MODS_FLAT_PLATE_AS_BEAM,
            BUILDING_MODS_INTERIOR_WALL_DENSITY,
            BUILDING_MODS_K_BRACING,
            BUILDING_MODS_LIFT_SLAB_CONSTRUCTION,
            BUILDING_MODS_PANEL_ZONE_DEFICIENCY,
            BUILDING_MODS_STRONG_COLUMN_WEAK_BEAM,
            BUILDING_MODS_WELD_STRENGTH,
          ]}
        >
          <legend>System Specific</legend>
          <Table className={buildingModifierTable}>
            <tbody>
              {yesNoRow(BUILDING_MODS_K_BRACING)}
              {yesNoRow(BUILDING_MODS_FLAT_PLATE_AS_BEAM)}
              {yesNoRow(BUILDING_MODS_CROSS_GRAIN_OOP_CONN)}
              {yesNoRow(BUILDING_MODS_INTERIOR_WALL_DENSITY)}
              {yesNoRow(BUILDING_MODS_LIFT_SLAB_CONSTRUCTION)}
              {customRow(BUILDING_MODS_DIAPHRAGM_DEFICIENCY)}
              {customRow(BUILDING_MODS_WELD_STRENGTH)}
              {customRow(BUILDING_MODS_STRONG_COLUMN_WEAK_BEAM)}
              {yesNoRow(BUILDING_MODS_PANEL_ZONE_DEFICIENCY)}
            </tbody>
          </Table>
        </FormGroup>
        <FormGroup
          tag="fieldset"
          form={form}
          fields={[
            BUILDING_MODS_CONSTRUCTION_QUALITY,
            BUILDING_MODS_NON_STRUCTURAL_CONSTRUCTION_QUALITY,
            BUILDING_MODS_MATERIAL_DETERIORATION,
          ]}
        >
          <legend>Construction Quality</legend>
          <Table className={buildingModifierTable}>
            <tbody>
              {constructionQualityRow(BUILDING_MODS_CONSTRUCTION_QUALITY)}
              {constructionQualityRow(BUILDING_MODS_NON_STRUCTURAL_CONSTRUCTION_QUALITY)}
              {structuralMaterialDeteriorationRow(BUILDING_MODS_MATERIAL_DETERIORATION)}
            </tbody>
          </Table>
        </FormGroup>
        <BackButton onClick={onBack} />
        <NextButton onClick={onNext} />
      </Form>
    </div>
  );
};

export default BuildingModifiersChecklistSection;
